import { TRANSLATION_MEMORY_COLLECTIONS_QUERY } from '@src/gql/translation-memory-gql';
import { extractErrorInfo } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';

import type ApiClient from '../apiClient/ApiClient';
import type { Client } from '@datocms/cma-client-browser';
import type { GraphQLClient } from 'graphql-request';

/** A flat translation-memory collection (PLAT-10837 `translation_memory_collection`). */
export interface TranslationMemoryCollection {
  id: string;
  name: string;
  description?: string | null;
  /** The person who created this collection (Dato `created_by_user` ref). */
  createdByUser?: { id: string; name?: string } | null;
}

/** Editable fields on a collection. */
export interface TranslationMemoryCollectionInput {
  name: string;
  description?: string | null;
}

interface CollectionsResponse {
  allTranslationMemoryCollections: TranslationMemoryCollection[];
}

/**
 * CRUD over `translation_memory_collection` — GraphQL read (filtered by
 * showcase), CMA writes. Follows the `SpeakerRepository` idiom: return `null`
 * on failure, log with `extractErrorInfo`, publish after each write so the
 * record leaves draft state. Deletion is a hard `items.destroy` (the model has
 * no `is_deleted` field — unlike `composition`'s soft delete).
 */
export class TranslationMemoryCollectionRepository {
  private dClient: Client | ApiClient;
  private gqlClient: GraphQLClient;
  private logger = LogManager.getLogger(
    'repository:TranslationMemoryCollectionRepository',
  );

  constructor(dClient: Client | ApiClient, gqlClient: GraphQLClient) {
    this.dClient = dClient;
    this.gqlClient = gqlClient;
  }

  /**
   * All collections for a showcase, ordered by name. Unlike the mutation
   * methods (which return null/false on failure), this **rethrows** on error so
   * the caller can distinguish a genuine empty workspace (`[]`) from a failed
   * load — the service maps the throw to an error `Result` and the store shows
   * an error/retry state instead of a misleading "no collections yet".
   */
  async fetchForShowcase(
    showcaseId: string,
  ): Promise<TranslationMemoryCollection[]> {
    try {
      const response = await this.gqlClient.request<CollectionsResponse>(
        TRANSLATION_MEMORY_COLLECTIONS_QUERY,
        { showcaseId },
      );
      return response.allTranslationMemoryCollections ?? [];
    } catch (error) {
      this.logger.error(
        'Error fetching translation memory collections',
        extractErrorInfo(error),
      );
      throw error;
    }
  }

  /** Create a collection under a showcase, stamping the creator person ref. */
  async create(
    showcaseId: string,
    input: TranslationMemoryCollectionInput,
    personId: string | null,
  ): Promise<TranslationMemoryCollection | null> {
    try {
      const itemType = await this.dClient.itemTypes.find(
        'translation_memory_collection',
      );
      const created = await this.dClient.items.create({
        item_type: { type: 'item_type', id: itemType.id },
        name: input.name,
        description: input.description ?? '',
        showcase: showcaseId,
        created_by_user: personId,
        last_updated_by_user: personId,
      });
      await this.dClient.items.publish(created.id);
      return {
        id: created.id,
        name: input.name,
        description: input.description ?? '',
      };
    } catch (error) {
      this.logger.error(
        'Error creating translation memory collection',
        extractErrorInfo(error),
      );
      return null;
    }
  }

  /** Update a collection's name/description, stamping the last-editor person ref. */
  async update(
    id: string,
    input: TranslationMemoryCollectionInput,
    personId: string | null,
  ): Promise<TranslationMemoryCollection | null> {
    try {
      const updated = await this.dClient.items.update(id, {
        name: input.name,
        description: input.description ?? '',
        last_updated_by_user: personId,
      });
      await this.dClient.items.publish(id);
      return {
        id: updated.id,
        name: input.name,
        description: input.description ?? '',
      };
    } catch (error) {
      this.logger.error(
        'Error updating translation memory collection',
        extractErrorInfo(error),
      );
      return null;
    }
  }

  /** Hard-delete a collection (no `is_deleted` field on the model). */
  async delete(id: string): Promise<boolean> {
    try {
      await this.dClient.items.destroy(id);
      return true;
    } catch (error) {
      this.logger.error(
        'Error deleting translation memory collection',
        extractErrorInfo(error),
      );
      return false;
    }
  }
}
